import { useContext, useEffect, useState } from 'react'

import { ChatContext } from '@oneToOne/shared/components/chat/ChatContainer/ChatContext'
import {
  textSnippetList,
  textSnippetListItem,
  textSnippetListItemPhrase,
  textSnippetListItemShortcut,
} from '@oneToOne/shared/components/twind/ChatInput/ChatInput.twind'
import { CHAT_INPUT_POP_OVER_LIST_ITEM } from '@oneToOne/shared/testIds'

import { UnstyledButton } from '@core/components/twind/Button/Button'
import { usePrevious } from '@core/hooks/usePrevious'
import { useTwindContext } from '@core/twind'

interface Props {
  value: string
  setValue: (value: string) => void
  dropdownStyle: Record<string, string>
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
}

/**
 * Chat Input Popover Component, shows a list of text snippets
 */
export const ChatInputPopover: React.FC<Props> = ({
  value,
  setValue,
  dropdownStyle,
  textareaRef,
}) => {
  const { tw } = useTwindContext()
  const { textSnippets } = useContext(ChatContext)

  const words = value.split(/\s+/)
  const lastWord = words[words.length - 1]

  const matchingTextSnippets = textSnippets?.filter((snippet) =>
    snippet.shortcut.startsWith(lastWord),
  )

  const prevValue = usePrevious(value)
  const [updatedDropdownStyle, setUpdatedDropdownStyle] =
    useState(dropdownStyle)
  const [replaced, setReplaced] = useState(false)

  useEffect(() => {
    if (
      matchingTextSnippets &&
      matchingTextSnippets.length &&
      prevValue !== value
    ) {
      setUpdatedDropdownStyle({
        ...dropdownStyle,
        top: `-${38 * matchingTextSnippets.length}px`,
      })
      setReplaced(false)
    }
  }, [dropdownStyle, matchingTextSnippets, value, prevValue])

  if (
    lastWord.length >= 2 &&
    matchingTextSnippets &&
    matchingTextSnippets.length &&
    !replaced
  ) {
    return (
      <div
        className={tw?.(
          textSnippetList(updatedDropdownStyle),
          'absolute',
          'flex',
          'flex-col',
          'z-10',
          'rounded',
        )}
      >
        {matchingTextSnippets.map((snippet) => (
          <UnstyledButton
            key={snippet.id}
            className={tw?.(textSnippetListItem, 'px-2', 'py-1.5')}
            onClick={() => {
              setValue(value.replace(lastWord, snippet.content))
              setReplaced(true)
              textareaRef.current?.focus()
            }}
            data-testid={CHAT_INPUT_POP_OVER_LIST_ITEM}
          >
            <span
              className={tw?.(
                textSnippetListItemShortcut,
                'text-sm',
                'font-bold',
              )}
            >
              {snippet.shortcut}
            </span>
            <span
              className={tw?.(textSnippetListItemPhrase, 'text-sm', 'ml-2')}
            >
              {snippet.content}
            </span>
          </UnstyledButton>
        ))}
      </div>
    )
  }

  return null
}
